import { memo } from "react";

import { isInPortal } from "utilities/portal/portal";
import { routes } from "utilities/routes";

import { BTIconExclamationCircleOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { useBillLinkAbsolute } from "commonComponents/utilities/Grid/bill/billLinkMode";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import {
    GridColumnType,
    type IClientGridColumn,
} from "commonComponents/utilities/Grid/GridContainer.types";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";
import { TextWrap } from "commonComponents/utilities/TextWrap/TextWrap";

import "./columnBillLink.less";

export interface IBillLinkResponse {
    builderId: number;
    jobId: number;
    id: number;
    title: string;
    isDuplicate: boolean;
}

export const BillLinkCell = memo(function BillLinkCell({
    cellValue,
    field,
}: IGridCellRenderProps<IBillLinkResponse>) {
    // When true, builder/sub portal links navigate to the standalone Bill page (absolute path);
    // when false they render a relative link that mounts the Bill modal over the current route.
    // Read from context so the Bills list can flip modes in place. Owner portal is unaffected —
    // it always renders a print-only link.
    const useAbsoluteLink = useBillLinkAbsolute();

    if (!cellValue) {
        return null;
    }

    const link = isInPortal({ owner: true }) ? (
        <BTLink
            shouldOpenNewTab
            useAutoSPARouting
            to={routes.bill.getPrintLink([cellValue.id], cellValue.jobId)}
        >
            <TextTruncate>{cellValue.title}</TextTruncate>
        </BTLink>
    ) : useAbsoluteLink ? (
        <BTLink
            to={routes.bill.getDetailsLink(cellValue.id, cellValue.jobId)}
            data-testid={field.full}
        >
            <TextTruncate>{cellValue.title}</TextTruncate>
        </BTLink>
    ) : (
        <BTLinkRelative
            to={routes.bill.getDetailsPath(cellValue.id, cellValue.jobId)}
            data-testid={field.full}
        >
            <TextTruncate>{cellValue.title}</TextTruncate>
        </BTLinkRelative>
    );

    return (
        <TextWrap className="BillLinkTitle">
            {cellValue.isDuplicate && (
                <BTIconExclamationCircleOutlined
                    className="padding-right-xs"
                    title="This bill could be a duplicate"
                />
            )}
            {link}
        </TextWrap>
    );
});

// Single stable module-level column. The absolute/relative choice now comes from
// BillLinkAbsoluteContext at render time, so the grid always sees the same Cell
// reference (avoids the header remount/sort races a per-mode component would cause).
export const columnBillLink: IClientGridColumn<IBillLinkResponse> = {
    columnType: GridColumnType.billLink,
    Cell: BillLinkCell,
};
